import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import clsx from "clsx";

import { Button } from "../../components/common/Button";
import { Input } from "../../components/common/Input";
import { Select } from "../../components/common/Select";
import { Spinner } from "../../components/common/Spinner";
import {
  getStorage, getChannelTitles, patchStorageBasic, patchRecordMode,
  type DiskInfo, type StorageBasic, type RecordModeEntry,
} from "../../api/config";
import type { Device } from "../../types";

const SNAP_MASK_ON = "63 00:00:00-24:00:00";
const SNAP_MASK_OFF = "0 00:00:00-24:00:00";
const SNAP_DAYS = 8;

const DISK_TYPE_LABEL_ES: Record<string, string> = {
  ReadWrite: "Lectura/Escritura",
  ReadOnly: "Solo lectura",
  Redundant: "Redundancia",
  Backup: "Respaldo",
};

const MODE_OPTS = [
  { value: "0", label: "Auto" },
  { value: "1", label: "Manual" },
  { value: "2", label: "Off" },
];

const FULL_OPTS = [
  { value: "true", label: "Sobreescribir" },
  { value: "false", label: "Detener grabación" },
];

const PACKET_OPTS = [
  { value: "0", label: "Por duración" },
  { value: "1", label: "Por tamaño" },
];

const HOLD_PRESETS = [
  { value: "0", label: "Nunca" },
  { value: "1", label: "1 día" },
  { value: "7", label: "7 días" },
  { value: "15", label: "15 días" },
  { value: "30", label: "30 días" },
  { value: "60", label: "60 días" },
  { value: "90", label: "90 días" },
];

function formatStorageSize(mb: number): string {
  const gb = mb / 1024;
  if (gb >= 1024) return `${(gb / 1024).toFixed(2)} TB`;
  if (gb >= 1) return `${gb.toFixed(1)} GB`;
  return `${mb} MB`;
}

function diskShortName(path: string): string {
  return path.replace(/^\/dev\//, "");
}

function stateBadgeCls(label: string): string {
  const s = (label || "").toLowerCase();
  if (s === "normal") return "bg-green-900/40 text-green-300 border-green-700";
  if (s.includes("error")) return "bg-red-900/40 text-red-300 border-red-700";
  if (s === "inicializando" || s === "formateando") return "bg-yellow-900/40 text-yellow-200 border-yellow-700";
  if (s === "-" || !s) return "bg-line text-muted border-line";
  return "bg-yellow-900/40 text-yellow-200 border-yellow-700";
}

interface Props {
  deviceId: number;
  device: Device;
}

const DEFAULT_BASIC: StorageBasic = {
  OverWrite: true,
  FileHoldTime: 0,
  PacketType: 0,
  PacketLength: 60,
  PacketSize: 1048576,
};

export function StorageTab({ deviceId, device: _device }: Props) {
  const [original, setOriginal] = useState<{ disks: DiskInfo[]; basic: StorageBasic; record_mode: RecordModeEntry[] } | null>(null);
  const [basic, setBasic] = useState<StorageBasic>(DEFAULT_BASIC);
  const [recMode, setRecMode] = useState<RecordModeEntry[]>([]);
  const [channelNames, setChannelNames] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [savingBasic, setSavingBasic] = useState(false);
  const [savingRec, setSavingRec] = useState(false);

  const reload = async () => {
    setLoading(true);
    try {
      const d = await getStorage(deviceId);
      const basicSafe: StorageBasic = (d.basic && "OverWrite" in d.basic)
        ? d.basic as StorageBasic
        : DEFAULT_BASIC;
      setOriginal({ disks: d.disks || [], basic: basicSafe, record_mode: d.record_mode || [] });
      setBasic({ ...basicSafe });
      setRecMode((d.record_mode || []).map(r => ({ ...r })));
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || "Error leyendo discos");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
    getChannelTitles(deviceId)
      .then((d) => setChannelNames(d.titles.map(t => t.name)))
      .catch(() => setChannelNames([]));
  }, [deviceId]);

  if (loading || !original) return <Spinner />;

  const disks = original.disks;
  const totalCapacity = disks.reduce((acc, d) => acc + d.total_mb, 0);
  const totalUsed = disks.reduce((acc, d) => acc + d.used_mb, 0);
  const totalFree = disks.reduce((acc, d) => acc + d.free_mb, 0);

  const saveBasic = async () => {
    setSavingBasic(true);
    try {
      const payload: Record<string, string> = {
        "StorageGroup[0].OverWrite": basic.OverWrite ? "true" : "false",
        "StorageGroup[0].FileHoldTime": String(basic.FileHoldTime),
        "MediaGlobal.PacketType": String(basic.PacketType),
        "MediaGlobal.PacketLength": String(basic.PacketLength),
        "MediaGlobal.PacketSize": String(basic.PacketSize),
      };
      await patchStorageBasic(deviceId, payload);
      toast.success("Configuración básica guardada");
      await reload();
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || "Error guardando");
    } finally {
      setSavingBasic(false);
    }
  };

  const updateCellMode = (ch: number, key: "Mode" | "ModeExtra1" | "ModeExtra2", value: number) => {
    setRecMode(p => p.map(r => r.channel === ch ? { ...r, [key]: value } : r));
  };
  const updateCellSnap = (ch: number, value: boolean) => {
    setRecMode(p => p.map(r => r.channel === ch ? { ...r, SnapOn: value } : r));
  };
  const applyColumn = (key: "Mode" | "ModeExtra1" | "ModeExtra2" | "SnapOn", value: number | boolean) => {
    setRecMode(p => p.map(r => ({ ...r, [key]: value })));
  };

  const saveRecord = async () => {
    setSavingRec(true);
    try {
      const payload: Record<string, string> = {};
      for (const r of recMode) {
        const ch = r.channel;
        payload[`RecordMode[${ch}].Mode`] = String(r.Mode);
        payload[`RecordMode[${ch}].ModeExtra1`] = String(r.ModeExtra1);
        payload[`RecordMode[${ch}].ModeExtra2`] = String(r.ModeExtra2);
        const orig = original.record_mode.find(o => o.channel === ch);
        if (orig && orig.SnapOn !== r.SnapOn) {
          for (let d = 0; d < SNAP_DAYS; d++) {
            payload[`Snap[${ch}].TimeSection[${d}][0]`] = r.SnapOn ? SNAP_MASK_ON : SNAP_MASK_OFF;
          }
        }
      }
      await patchRecordMode(deviceId, payload);
      toast.success("Modo de grabación guardado");
      await reload();
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || "Error guardando");
    } finally {
      setSavingRec(false);
    }
  };

  return (
    <div className="space-y-4 max-w-5xl">
      <div>
        <h2 className="text-base font-semibold">Discos</h2>
        <p className="text-xs text-muted mt-0.5">Almacenamiento del NVR: estado de discos, opciones básicas y modo de grabación.</p>
      </div>

      <section className="border border-line rounded p-3 bg-panel/30 space-y-2">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold">Administración de discos</h3>
          {disks.length > 0 && (
            <div className="text-xs text-muted text-right">
              <div>{disks.length} disco{disks.length === 1 ? "" : "s"} · {formatStorageSize(totalCapacity)} total</div>
              <div>{formatStorageSize(totalUsed)} usado · {formatStorageSize(totalFree)} libre</div>
            </div>
          )}
        </div>
        <table className="w-full text-sm border border-line">
          <thead className="bg-panel">
            <tr className="text-xs text-muted">
              <th className="text-left px-3 py-2">Nombre</th>
              <th className="text-left px-3 py-2">Estado</th>
              <th className="text-left px-3 py-2">Tipo</th>
              <th className="text-right px-3 py-2">Particiones</th>
              <th className="text-right px-3 py-2">Total</th>
              <th className="text-right px-3 py-2">Usado</th>
              <th className="text-right px-3 py-2">Libre</th>
            </tr>
          </thead>
          <tbody>
            {disks.length === 0 && (
              <tr><td colSpan={7} className="p-4 text-center text-muted">Sin discos detectados</td></tr>
            )}
            {disks.map((d, i) => (
              <tr key={i} className="border-t border-line">
                <td className="px-3 py-2 font-mono">{diskShortName(d.name)}</td>
                <td className="px-3 py-2">
                  <span className={clsx("inline-block px-2 py-0.5 rounded border text-xs", stateBadgeCls(d.state_label))}>
                    {d.state_label}
                  </span>
                </td>
                <td className="px-3 py-2 text-muted">
                  {DISK_TYPE_LABEL_ES[d.type] ?? d.type ?? "-"}
                </td>
                <td className="px-3 py-2 text-right text-muted">{d.partitions ?? "-"}</td>
                <td className="px-3 py-2 text-right">{formatStorageSize(d.total_mb)}</td>
                <td className="px-3 py-2 text-right">{formatStorageSize(d.used_mb)}</td>
                <td className="px-3 py-2 text-right">{formatStorageSize(d.free_mb)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="text-xs text-muted">
          El tipo de disco es de solo lectura: la API HTTP V3.98 no documenta cómo cambiarlo. Pendiente de investigación contra el NVR.
        </p>
      </section>

      <section className="border border-line rounded p-3 bg-panel/30 space-y-3">
        <h3 className="text-sm font-semibold">Básico</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <Select
            label="Disco lleno"
            options={FULL_OPTS}
            value={basic.OverWrite ? "true" : "false"}
            onChange={(e) => setBasic({ ...basic, OverWrite: e.target.value === "true" })}
          />
          <Select
            label="Crear archivos de video"
            options={PACKET_OPTS}
            value={String(basic.PacketType)}
            onChange={(e) => setBasic({ ...basic, PacketType: Number(e.target.value) })}
          />
          {basic.PacketType === 0 ? (
            <Input
              label="Duración (min)"
              type="number"
              min={1}
              max={255}
              value={String(basic.PacketLength)}
              onChange={(e) => setBasic({ ...basic, PacketLength: Math.max(1, Math.min(255, Number(e.target.value) || 1)) })}
            />
          ) : (
            <Input
              label="Tamaño (MB)"
              type="number"
              min={1}
              max={4096}
              value={String(Math.round(basic.PacketSize / 1024))}
              onChange={(e) => setBasic({ ...basic, PacketSize: Math.max(1, Math.min(4096, Number(e.target.value) || 1)) * 1024 })}
            />
          )}
          <Select
            label="Borrar archivos expirados"
            options={HOLD_PRESETS}
            value={String(basic.FileHoldTime)}
            onChange={(e) => setBasic({ ...basic, FileHoldTime: Number(e.target.value) })}
          />
          <div>
            <label className="block text-xs text-muted mb-1">Estrategia de reposo</label>
            <select
              disabled
              className="w-full bg-line text-muted border border-line rounded px-2 py-1.5 text-sm cursor-not-allowed"
              title="No soportado por la API HTTP de este firmware"
            >
              <option>Auto</option>
            </select>
          </div>
        </div>
        <div>
          <Button variant="primary" onClick={saveBasic} loading={savingBasic}>Guardar básico</Button>
        </div>
      </section>

      <section className="border border-line rounded p-3 bg-panel/30 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold">Modo de grabación</h3>
          <span className="text-xs text-muted">Auto = según schedule · Manual = grabación continua · Off = no grabar</span>
        </div>
        <table className="w-full text-sm border border-line">
          <thead className="bg-panel">
            <tr className="text-xs text-muted">
              <th className="text-left px-2 py-2">Canal</th>
              <th className="text-left px-2 py-2">Corriente (Main)</th>
              <th className="text-left px-2 py-2">Sub1</th>
              <th className="text-left px-2 py-2">Sub2</th>
              <th className="text-left px-2 py-2">Foto</th>
            </tr>
          </thead>
          <tbody>
            {recMode.map((r) => (
              <tr key={r.channel} className="border-t border-line">
                <td className="px-2 py-2 text-muted">
                  CH{r.channel + 1}{channelNames[r.channel] ? ` — ${channelNames[r.channel]}` : ""}
                </td>
                <td className="px-2 py-1">
                  <Select
                    options={MODE_OPTS}
                    value={String(r.Mode)}
                    onChange={(e) => updateCellMode(r.channel, "Mode", Number(e.target.value))}
                  />
                </td>
                <td className="px-2 py-1">
                  <Select
                    options={MODE_OPTS}
                    value={String(r.ModeExtra1)}
                    onChange={(e) => updateCellMode(r.channel, "ModeExtra1", Number(e.target.value))}
                  />
                </td>
                <td className="px-2 py-1">
                  <Select
                    options={MODE_OPTS}
                    value={String(r.ModeExtra2)}
                    onChange={(e) => updateCellMode(r.channel, "ModeExtra2", Number(e.target.value))}
                  />
                </td>
                <td className="px-2 py-2">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={r.SnapOn}
                      onChange={(e) => updateCellSnap(r.channel, e.target.checked)}
                    />
                    <span className="text-xs">{r.SnapOn ? "Encendido" : "Off"}</span>
                  </label>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="flex flex-wrap gap-2 text-xs">
          <Button size="sm" variant="ghost" onClick={() => applyColumn("Mode", 0)}>Main: Todo Auto</Button>
          <Button size="sm" variant="ghost" onClick={() => applyColumn("Mode", 2)}>Main: Todo Off</Button>
          <Button size="sm" variant="ghost" onClick={() => applyColumn("ModeExtra1", 0)}>Sub1: Todo Auto</Button>
          <Button size="sm" variant="ghost" onClick={() => applyColumn("ModeExtra1", 2)}>Sub1: Todo Off</Button>
          <Button size="sm" variant="ghost" onClick={() => applyColumn("ModeExtra2", 0)}>Sub2: Todo Auto</Button>
          <Button size="sm" variant="ghost" onClick={() => applyColumn("ModeExtra2", 2)}>Sub2: Todo Off</Button>
          <Button size="sm" variant="ghost" onClick={() => applyColumn("SnapOn", true)}>Foto: Encendido</Button>
          <Button size="sm" variant="ghost" onClick={() => applyColumn("SnapOn", false)}>Foto: Off</Button>
        </div>
        <div className="flex gap-2 pt-2">
          <Button variant="primary" onClick={saveRecord} loading={savingRec}>Guardar modo grabación</Button>
          <Button variant="ghost" onClick={reload} disabled={savingRec}>Restablecer</Button>
        </div>
      </section>
    </div>
  );
}
